import { Immutable } from "immer";
import React, { useId } from "react";
import { t } from "ttag";

import { IProductID } from "../../../models/nominals";
import { useUpsellAnalytics } from "../hooks/useUpsellAnalytics";

import styles from "./index.module.scss";

/**
 * Radio-style upsell card used only for `BundleGroupTypes.UPGRADE_PRODUCT`
 * bundles (currently the 1-800-Pillows PDP). Unlike `ConfiguratorUpsell`,
 * this variant deliberately omits `bundleType`, `configuratorType`, and the
 * info-modal/flair/cardImage props because none of them apply to the
 * Standard-vs-Upgrade radio pattern. If a future caller needs this
 * component for a non-UPGRADE_PRODUCT bundle, extend the props (and
 * `ConfiguratorUpsellContainer`'s gating) before reusing it.
 *
 * Known limitation: when `isSelected && isUnavailable` (a previously-selected
 * upgrade transitions to unavailable, e.g. via a sibling-option change), this
 * component renders the upgrade radio as checked + disabled but does NOT
 * auto-call `clearUpgrade()`. Auto-deselection is intentionally left to a
 * higher layer (e.g. configurator availability sync) so this presentation
 * component stays free of side-effects driven by external state transitions.
 * Callers are responsible for clearing the Redux selection when an upgrade
 * becomes unavailable.
 */
export const ConfiguratorUpsellRadio = (
    props: Immutable<{
        bundleID: number | null;
        sectionTitle: string | null;
        lineOne: string | null;
        lineThree: string | null;
        productUpgradeID: IProductID;
        isSelected: boolean;
        isUnavailable?: boolean;
    }>,
): React.ReactNode => {
    const configName = props.lineOne || props.sectionTitle || "";
    const { selectUpgrade, clearUpgrade } = useUpsellAnalytics(configName);

    // useId() guarantees a unique fallback per instance so two radios with
    // a null bundleID can't accidentally form one cross-instance group.
    const fallbackId = useId();
    const groupName = `configurator-upsell-radio-${props.bundleID ?? fallbackId}`;

    const onSelectStandard = () => clearUpgrade();
    const onSelectUpgrade = () => selectUpgrade(props.productUpgradeID);

    const sectionTitleId = props.sectionTitle
        ? `${groupName}-title`
        : undefined;
    return (
        <div className={styles.container}>
            {props.sectionTitle && (
                <h2
                    id={sectionTitleId}
                    className={`${styles.sectionTitle} configurator-upsell__section-title`}
                >
                    {props.sectionTitle}
                </h2>
            )}
            <div
                className={styles.radioGroup}
                role="radiogroup"
                aria-labelledby={sectionTitleId}
                aria-label={
                    sectionTitleId
                        ? undefined
                        : (props.lineOne ?? t`Upgrade options`)
                }
            >
                <label className={styles.option}>
                    <input
                        className={styles.input}
                        type="radio"
                        name={groupName}
                        checked={!props.isSelected}
                        onChange={onSelectStandard}
                    />
                    <span className={styles.label}>{t`Standard`}</span>
                </label>
                <label
                    className={`${styles.option}${props.isUnavailable ? ` ${styles.optionUnavailable}` : ""}`}
                >
                    <input
                        className={styles.input}
                        type="radio"
                        name={groupName}
                        checked={props.isSelected}
                        onChange={onSelectUpgrade}
                        disabled={props.isUnavailable}
                    />
                    <span className={styles.label}>
                        {props.lineOne ?? props.sectionTitle ?? t`Upgrade`}
                    </span>
                    {props.isUnavailable ? (
                        <span className={styles.unavailable}>
                            {t`Not Available`}
                        </span>
                    ) : (
                        props.lineThree && (
                            <span className={styles.price}>
                                {props.lineThree}
                            </span>
                        )
                    )}
                </label>
            </div>
        </div>
    );
};
